// hooks/useFirebaseUpload.ts
import React from 'react';
import { FirebaseError } from 'firebase/app';
import { User, signInAnonymously } from 'firebase/auth';
import {
  ref,
  deleteObject,
  uploadBytesResumable,
  getDownloadURL,
  getMetadata,
  FirebaseStorage,
  UploadTaskSnapshot,
  UploadTask,
} from 'firebase/storage';
import { auth, storage } from '@/lib/firebaseConfig';
import { v4 as uuidv4 } from 'uuid';
import { getStorageObjectName } from '@/helpers/document-name';
import {
  createFileMetadata,
  softDeleteFile,
} from '@/features/documents/services/fileMetadataService';
import {
  getPdfPasswordStatus,
  getProtectedPdfSource,
} from '../utils/protectedPdfSource';
import { extractStorageUid, replaceStorageUid } from '../utils/storageKey';

let pendingSignIn: Promise<User> | null = null;

const documentUploadCache = new WeakMap<
  File,
  Promise<{ url: string; key: string }>
>();

interface UploadFileOptions {
  includeDownloadURL?: boolean;
  fileName?: string;
  requireMetadata?: boolean;
  expectedUid?: string;
  persistOnly?: boolean;
  passwordProtected?: boolean;
}

interface StartUploadOptions extends UploadFileOptions {
  hidden?: boolean;
}

interface ActiveUpload {
  cancelled: boolean;
  task: UploadTask | null;
}

const createUploadCancelledError = (): DOMException =>
  new DOMException('Upload cancelled.', 'AbortError');

const ensureSignedIn = (): Promise<User> => {
  const currentUser = auth.currentUser;
  if (currentUser) return Promise.resolve(currentUser);

  if (!pendingSignIn) {
    pendingSignIn = signInAnonymously(auth)
      .then((cred) => cred.user)
      .finally(() => {
        pendingSignIn = null;
      });
  }

  return pendingSignIn;
};

/**
 * Interface for the return value of useFirebaseUpload hook.
 */
export interface UseFirebaseUploadReturn {
  progress: number; // Upload progress percentage.
  error: FirebaseError | Error | null; // Error object if an error occurs.
  metadataError: Error | null;
  downloadURL: string | null; // URL of the uploaded file.
  uploadFile: (
    file: File,
    path?: string,
    options?: UploadFileOptions
  ) => Promise<{ url: string; key: string }>; // Function to initiate the file upload.
  cancelUpload: () => void; // Cancels the currently in-flight upload task, if any.
}

/**
 * Custom hook to handle file uploads using Firebase Storage.
 *
 * @returns {UseFirebaseUploadReturn} Object containing upload progress, error, download URL, and the uploadFile function.
 */
const useFirebaseUpload = (
  targetStorage: FirebaseStorage | null = storage
): UseFirebaseUploadReturn => {
  // State to store upload progress percentage.
  const [progress, setProgress] = React.useState<number>(0);
  // State to store any errors encountered during upload.
  const [error, setError] = React.useState<FirebaseError | Error | null>(null);
  // State to store the download URL of the uploaded file.
  const [downloadURL, setDownloadURL] = React.useState<string | null>(null);
  const [metadataError, setMetadataError] = React.useState<Error | null>(null);
  // Ref to hold the current upload task for cancellation.
  const activeUploadRef = React.useRef<ActiveUpload | null>(null);

  /**
   * Function to upload a file to Firebase Storage.
   *
   * @param {File} file - The file to be uploaded.
   * @param {string} [path='uploads/'] - The path in the storage bucket where the file will be stored.
   */
  const startUpload = React.useCallback(
    (
      file: File,
      path: string = 'documents/',
      {
        includeDownloadURL = true,
        fileName,
        requireMetadata = false,
        expectedUid,
        hidden = false,
        passwordProtected,
      }: StartUploadOptions = {}
    ) =>
      new Promise<{ url: string; key: string }>(async (resolve, reject) => {
        if (!file) {
          reject(new Error('No file provided.'));
          return;
        }

        if (!targetStorage) {
          reject(new Error('Firebase Storage is not configured.'));
          return;
        }

        const activeUpload: ActiveUpload = { cancelled: false, task: null };
        activeUploadRef.current = activeUpload;

        const releaseActiveUpload = () => {
          if (activeUploadRef.current === activeUpload) {
            activeUploadRef.current = null;
          }
        };

        try {
          setProgress(0);
          setError(null);
          setMetadataError(null);

          const currentUser = await ensureSignedIn();

          if (activeUpload.cancelled) {
            releaseActiveUpload();
            reject(createUploadCancelledError());
            return;
          }

          const uid = currentUser.uid;

          if (expectedUid && uid !== expectedUid) {
            throw new Error('Authenticated Firebase user does not match upload owner.');
          }

          const fileId = uuidv4();
          const createdAtMs = Date.now();
          const namePrefix = `${fileId}-${createdAtMs}-`;
          const storageFileName =
            fileName ??
            `${namePrefix}${getStorageObjectName(file.name)}`;
          // Prepend users/${uid}/ to ensure isolation and match storage rules
          const fullPath = `users/${uid}/${path}${storageFileName}`;
          const fileRef = ref(targetStorage, fullPath);
          const uploadTask = uploadBytesResumable(fileRef, file);

          // Store the current upload task in the ref. This is used for cancellation.
          activeUpload.task = uploadTask;

          const rollbackUploadedObject = () =>
            deleteObject(uploadTask.snapshot.ref).catch((rollbackError) => {
              console.error('Cancelled upload rollback failed:', rollbackError);
            });

          const rejectIfCancelled = async (): Promise<boolean> => {
            if (!activeUpload.cancelled) return false;
            await rollbackUploadedObject();
            reject(createUploadCancelledError());
            return true;
          };

          uploadTask.on(
            'state_changed',
            (snapshot: UploadTaskSnapshot) => {
              const progressPercent = Math.round(
                (snapshot.bytesTransferred / snapshot.totalBytes) * 100
              );
              setProgress(progressPercent);
            },
            (err: FirebaseError | Error) => {
              releaseActiveUpload();

              if (activeUpload.cancelled) {
                reject(createUploadCancelledError());
                return;
              }

              setError(err);
              reject(err);
              console.error('Upload error:', err);
            },
            async () => {
              try {
                if (await rejectIfCancelled()) return;

                const url = includeDownloadURL
                  ? await getDownloadURL(uploadTask.snapshot.ref)
                  : '';

                if (await rejectIfCancelled()) return;

                if (path.startsWith('documents')) {
                  try {
                    const metadataInput = {
                      uid,
                      fileId,
                      fileName: file.name,
                      storagePath: fullPath,
                      size: file.size,
                      mimeType: file.type,
                      createdAtMs,
                      ...(passwordProtected !== undefined
                        ? { passwordProtected }
                        : {}),
                    };

                    if (hidden) {
                      await createFileMetadata({
                        ...metadataInput,
                        hidden: true,
                      }).catch(async () => {
                        await createFileMetadata(metadataInput);
                        await softDeleteFile(uid, fileId);
                      });
                    } else {
                      await createFileMetadata(metadataInput);
                    }

                    if (activeUpload.cancelled) {
                      await softDeleteFile(uid, fileId).catch(
                        (rollbackError) => {
                          console.error(
                            'Cancelled upload metadata rollback failed:',
                            rollbackError
                          );
                        }
                      );
                      await rollbackUploadedObject();
                      reject(createUploadCancelledError());
                      return;
                    }
                  } catch (metaErr: any) {
                    console.error('File metadata create failed:', metaErr);
                    setMetadataError(metaErr as Error);
                    if (requireMetadata || hidden) {
                      await deleteObject(uploadTask.snapshot.ref).catch(
                        (rollbackError) => {
                          console.error(
                            'Uploaded file rollback failed:',
                            rollbackError
                          );
                        }
                      );
                      throw metaErr;
                    }
                  }
                }

                if (url) setDownloadURL(url);
                resolve({
                  url,
                  key: fullPath,
                });
              } catch (err: any) {
                setError(err);
                reject(err);
              } finally {
                releaseActiveUpload();
              }
            }
          );
        } catch (err: any) {
          releaseActiveUpload();
          setError(err);
          reject(err);
        }
      }),

    [targetStorage]
  );

  const reconcileCachedUpload = React.useCallback(
    (
      cached: Promise<{ url: string; key: string }>,
      file: File,
      path: string,
      options: StartUploadOptions
    ) =>
      cached.then(async (result) => {
        const currentUser = await ensureSignedIn();
        const storageUid = extractStorageUid(result.key);

        if (!storageUid || storageUid === currentUser.uid) {
          return result;
        }

        if (options.expectedUid && currentUser.uid !== options.expectedUid) {
          throw new Error(
            'Authenticated Firebase user does not match upload owner.'
          );
        }

        if (!targetStorage) {
          return startUpload(file, path, options);
        }

        const migratedKey = replaceStorageUid(
          result.key,
          storageUid,
          currentUser.uid
        );
        const migratedRef = ref(targetStorage, migratedKey);

        try {
          let url = '';

          if (options.includeDownloadURL === false) {
            await getMetadata(migratedRef);
          } else {
            url = await getDownloadURL(migratedRef);
          }

          return { url, key: migratedKey };
        } catch (error) {
          return startUpload(file, path, options);
        }
      }),
    [startUpload, targetStorage]
  );

  const uploadFile = React.useCallback(
    (
      file: File,
      path: string = 'documents/',
      options: UploadFileOptions = {}
    ) => {
      if (!file) {
        return Promise.reject(new Error('No file provided.'));
      }

      const isDocumentUpload = path.startsWith('documents');
      const { persistOnly, ...uploadOptions } = options;

      const uploadCached = (
        target: File,
        targetOptions: StartUploadOptions
      ) => {
        if (!isDocumentUpload) {
          return startUpload(target, path, targetOptions);
        }

        const cached = documentUploadCache.get(target);
        const uploadPromise = cached
          ? reconcileCachedUpload(cached, target, path, targetOptions)
          : startUpload(target, path, targetOptions);

        documentUploadCache.set(target, uploadPromise);
        uploadPromise.catch(() => {
          if (documentUploadCache.get(target) === uploadPromise) {
            documentUploadCache.delete(target);
          }
        });

        return uploadPromise;
      };

      const protectedSource = isDocumentUpload
        ? getProtectedPdfSource(file)
        : undefined;

      if (!protectedSource) {
        return uploadCached(file, {
          ...uploadOptions,
          passwordProtected:
            uploadOptions.passwordProtected ?? getPdfPasswordStatus(file),
        });
      }

      const sourceUpload = uploadCached(protectedSource, {
        ...uploadOptions,
        passwordProtected: true,
      });

      if (persistOnly) {
        return sourceUpload;
      }

      const workingUpload = uploadCached(file, {
        ...uploadOptions,
        hidden: true,
      });

      return Promise.all([sourceUpload, workingUpload]).then(
        ([, workingResult]) => workingResult
      );
    },
    [reconcileCachedUpload, startUpload]
  );

  // Cleanup: if the component unmounts, cancel the ongoing upload.
  React.useEffect(() => {
    return () => {
      const activeUpload = activeUploadRef.current;
      if (!activeUpload) return;

      activeUpload.cancelled = true;
      activeUpload.task?.cancel();
    };
  }, []);

  const cancelUpload = React.useCallback(() => {
    const activeUpload = activeUploadRef.current;
    if (!activeUpload) return;

    activeUpload.cancelled = true;
    activeUpload.task?.cancel();
  }, []);

  // Return the states and the uploadFile function for use in components.
  return { progress, error, metadataError, downloadURL, uploadFile, cancelUpload };
};

export default useFirebaseUpload;
